import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Shared bottom-detection tolerance. Must stay in lockstep with the
 * `atBottomThreshold` passed to the chat `<Virtuoso>` instances: the hook
 * re-arms auto-follow on the same boundary at which those components hide the
 * scroll-to-bottom button. If the two diverged, a resting position inside the
 * gap would hide the arrow while leaving auto-follow OFF, so new messages would
 * silently land below the viewport.
 */
export const AT_BOTTOM_THRESHOLD_PX = 4;

/**
 * Chat "stick to bottom" — state machine with two states:
 *   ON  → auto-scroll new messages all the way down to display them fully.
 *   OFF → user is reading history; new messages stay where they are.
 *
 * Transitions:
 *   * user scrolls UP (away from bottom)                       → OFF
 *   * scroll reaches the bottom (within AT_BOTTOM_THRESHOLD_PX) → ON
 *   * `resetUserScrolledUp()` (player typed/sent)              → ON
 */
export function useStickToBottom() {
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const userScrolledUpRef = useRef(false);
  userScrolledUpRef.current = userScrolledUp;

  const cleanupRef = useRef<(() => void) | null>(null);

  const setScroller = useCallback((el: HTMLElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!el) return;

    let lastScrollTop = el.scrollTop;

    const onScroll = () => {
      const currentScrollTop = el.scrollTop;
      const wentUp = currentScrollTop < lastScrollTop;
      lastScrollTop = currentScrollTop;

      const distance = el.scrollHeight - currentScrollTop - el.clientHeight;
      // Re-arm at the SAME tolerance the chat <Virtuoso> uses to hide the
      // scroll-to-bottom button (atBottomThreshold). A sub-threshold resting
      // position (sub-pixel / fractional after a smooth scroll) otherwise hides
      // the arrow while leaving auto-follow OFF, stranding new messages below
      // the fold. `userScrolledUp` only flips ON via `wentUp && distance > 0`,
      // so widening the clear tolerance can't cause spurious OFF→ON flips.
      const atBottom = distance <= AT_BOTTOM_THRESHOLD_PX;

      if (atBottom) {
        if (userScrolledUpRef.current) {
          userScrolledUpRef.current = false;
          setUserScrolledUp(false);
        }
      } else if (wentUp && !userScrolledUpRef.current) {
        userScrolledUpRef.current = true;
        setUserScrolledUp(true);
      }
    };

    el.addEventListener('scroll', onScroll, { passive: true });

    cleanupRef.current = () => {
      el.removeEventListener('scroll', onScroll);
    };
  }, []);

  const resetUserScrolledUp = useCallback(() => {
    userScrolledUpRef.current = false;
    setUserScrolledUp(false);
  }, []);

  useEffect(() => () => cleanupRef.current?.(), []);

  return { userScrolledUp, userScrolledUpRef, setScroller, resetUserScrolledUp };
}
